import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Box, Button, Typography } from "@mui/material";

import { selectCurrentAccount, selectUserId } from "fond/api";
import { SwitchAccount, useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import { AccountSelectionButton } from "fond/topBar/accountMenuItems.styles";
import { getAccountContext } from "fond/utils/accountContextStorage";
import AccountAvatar from "fond/widgets/AccountAvatar";
import { AccountTypeChip } from "fond/widgets/AccountSwitch";

const ROW_HEIGHT = 50;
const MAX_ROW = 10;
const MAX_WIDTH = 300;
const MAX_LIST_WIDTH = MAX_WIDTH - 140; // Account avatar width + Member/External chip width

interface IAccountRowProps extends ListChildComponentProps<SwitchAccount[]> {
  onClick: () => void;
  onAccountClick: (accountId: string, accountName: string) => void;
  selectedAccountId: string;
}

const accountRow = ({ data: accounts, index, style, onClick, onAccountClick, selectedAccountId }: IAccountRowProps) => {
  const account = accounts[index];

  const onClickRow = () => {
    onClick(); // Close the menu first
    onAccountClick(account.ID, account.Name);
  };

  return (
    <AccountSelectionButton
      style={style}
      key={account.ID}
      onClick={onClickRow}
      selected={account.ID === selectedAccountId}
      data-testid="account-items"
    >
      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <AccountAvatar account={account} variant="rounded" sx={{ width: 32, height: 32 }} data-testid="account-avatar" />
        <Typography noWrap maxWidth={MAX_LIST_WIDTH} data-testid={`account-name-${account.ID}`}>
          {account.Name}
        </Typography>
      </Box>
      <AccountTypeChip external={account.External} role={account.Role} />
    </AccountSelectionButton>
  );
};

interface IProps {
  onAccountSwitch: () => void;
  onAccountClick: (accountId: string, accountName: string) => void;
}

const AccountMenuItems: React.FC<IProps> = ({ onAccountSwitch, onAccountClick }) => {
  const userId = useSelector(selectUserId);
  const selectedAccountId: string | undefined = useSelector(selectCurrentAccount)?.ID || getAccountContext(userId);
  const navigate = useNavigate();

  const { accounts: displayedAccounts } = useSwitchableAccounts({ mode: "pinned-only-or-all" });
  const listHeight = (displayedAccounts.length >= MAX_ROW ? MAX_ROW : displayedAccounts.length) * ROW_HEIGHT;

  const handleViewOtherAccounts = () => {
    onAccountSwitch(); // Close the menu
    navigate("/accounts");
  };

  return (
    <Box width={MAX_WIDTH} data-testid="account-list">
      {displayedAccounts?.length > 0 && selectedAccountId && (
        <FixedSizeList<SwitchAccount[]>
          itemData={displayedAccounts}
          itemSize={ROW_HEIGHT}
          height={listHeight}
          width={MAX_WIDTH}
          overscanCount={20}
          itemCount={displayedAccounts.length}
          className="customScrollbars"
        >
          {(fixedProps) =>
            accountRow({
              ...fixedProps,
              onClick: onAccountSwitch,
              onAccountClick,
              selectedAccountId,
            })
          }
        </FixedSizeList>
      )}
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        <Button variant="text" fullWidth onClick={handleViewOtherAccounts} data-testid="view-other-accounts-button">
          Manage accounts
        </Button>
      </Box>
    </Box>
  );
};

export default AccountMenuItems;
